
import React from 'react';
import { Award, Star, Trophy } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { format } from 'date-fns';
import { ActivityLog } from '@/models/LearnTypes';

interface ActivityTabProps {
  activityLogs: ActivityLog[];
  isLoadingActivities: boolean;
}

const ActivityTab: React.FC<ActivityTabProps> = ({
  activityLogs,
  isLoadingActivities
}) => {
  const formatActivityDate = (date: Date) => {
    return format(date, 'MMM d, yyyy h:mm a');
  };
  
  const getActivityIcon = (type: string, levelType?: string) => {
    if (type === 'level-completed') {
      switch (levelType) {
        case 'flashcard':
          return <Award className="h-4 w-4 text-primary" />;
        case 'reverse-flashcard':
          return <Award className="h-4 w-4 text-indigo-500" />;
        case 'spelling':
          return <Award className="h-4 w-4 text-green-500" />;
        default:
          return <Award className="h-4 w-4 text-muted-foreground" />;
      }
    } else {
      return <Trophy className="h-4 w-4 text-yellow-400" />;
    }
  };
  
  const getActivityTitle = (activity: ActivityLog) => {
    if (activity.type === 'level-completed') {
      const levelIndex = activity.levelIndex !== undefined ? activity.levelIndex + 1 : '?';
      return `Level ${levelIndex} Complete: ${activity.lessonName}`;
    } else {
      return `Lesson Complete: ${activity.lessonName}`;
    }
  };

  // Helper to display XP with bonus details for any activity type
  const renderXpDisplay = (activity: ActivityLog) => {
    // Check if this is a perfect lesson completion
    if (activity.type === 'lesson-completed' && 
        activity.isPerfectLesson && 
        activity.baseXpEarned !== undefined && 
        activity.perfectLessonBonus) {
      // Show base XP + bonus breakdown for perfect lessons
      return (
        <span className="text-sm font-medium text-yellow-500 flex items-center gap-1">
          <Star className="h-3 w-3" /> 
          +{activity.xpEarned} XP
          <span className="text-xs text-green-500">
            (includes +{activity.perfectLessonBonus} bonus)
          </span>
        </span>
      );
    } 
    // Check if this is a perfect level completion
    else if (activity.type === 'level-completed' && 
             activity.isPerfect && 
             activity.baseXpEarned !== undefined && 
             activity.perfectLevelBonus) {
      // Show base XP + bonus breakdown for perfect levels
      return (
        <span className="text-sm font-medium text-yellow-500 flex items-center gap-1">
          <Star className="h-3 w-3" /> 
          +{activity.xpEarned} XP
          <span className="text-xs text-green-500">
            (includes +{activity.perfectLevelBonus} bonus)
          </span>
        </span>
      );
    } 
    // Standard XP display for non-perfect or activities without bonus details
    else {
      return (
        <span className="text-sm font-medium text-yellow-500 flex items-center gap-1">
          <Star className="h-3 w-3" /> +{activity.xpEarned} XP
        </span>
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity History</CardTitle>
        <CardDescription>Track your learning journey</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 max-h-96 overflow-y-auto">
        {isLoadingActivities ? (
          <div className="text-center py-4 text-muted-foreground">Loading activity...</div>
        ) : activityLogs.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">No activity yet. Start learning!</div>
        ) : (
          activityLogs.map((activity, index) => (
            <div key={activity.id || index} className="flex gap-3 border-b border-muted pb-3 last:border-0">
              <div className="mt-1">
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                  {getActivityIcon(activity.type, activity.levelType)}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex flex-col">
                  <span className="font-medium">{getActivityTitle(activity)}</span>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      {formatActivityDate(activity.timestamp)}
                    </span>
                    {renderXpDisplay(activity)}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityTab;
